import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, User, Lock, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Skel, SkeletonRegion } from '../components/ui/PageState';

export default function SetupAccount() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    // If user already completed setup, redirect to dashboard
    const meta = user.user_metadata;
    if (meta?.full_name && meta?.setup_completed) {
      navigate('/', { replace: true });
      return;
    }

    const fetchOrgName = async () => {
      try {
        const { data } = await supabase
          .from('organization_members')
          .select('organizations(name)')
          .eq('user_id', user.id)
          .limit(1)
          .single();
        if (data && (data as any).organizations) {
          setOrgName((data as any).organizations.name);
        }
      } catch {
        // Ignore - org name is optional for display
      }
    };

    // Get the org name from the invite metadata
    const invitedOrgName = meta?.invited_org_name;
    if (invitedOrgName) {
      setOrgName(invitedOrgName);
    } else {
      fetchOrgName();
    }
  }, [user, navigate]);

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (!fullName.trim()) {
      setError('Por favor, informe seu nome completo.');
      return;
    }

    setLoading(true);

    try {
      // Update password
      const { error: passwordError } = await supabase.auth.updateUser({
        password,
      });

      if (passwordError) {
        throw passwordError;
      }

      // Update user metadata with full name and setup flag
      const { error: metaError } = await supabase.auth.updateUser({
        data: {
          full_name: fullName.trim(),
          setup_completed: true,
        },
      });

      if (metaError) {
        throw metaError;
      }

      // Redirect to dashboard
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Erro ao configurar conta.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="auth-container">
        <div className="glass-card setup-card">
          <SkeletonRegion label="Carregando seu convite…">
            <div className="sk-stack" style={{ alignItems: 'center', marginBottom: '1.5rem' }}>
              <Skel className="sk-circle" w={64} h={64} />
              <Skel w="65%" h={26} />
              <Skel w="80%" h={14} />
            </div>
            <div className="sk-stack">
              <Skel h={46} style={{ borderRadius: 8 }} />
              <Skel h={46} style={{ borderRadius: 8 }} />
              <Skel h={46} style={{ borderRadius: 8 }} />
            </div>
          </SkeletonRegion>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="glass-card setup-card">
        <div className="setup-header">
          <div className="setup-icon">
            <CheckCircle2 size={32} />
          </div>
          <h1>Bem-vindo ao Seu.Video!</h1>
          {orgName ? (
            <p className="subtitle">
              <Building2 size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Você foi convidado para <strong>{orgName}</strong>
            </p>
          ) : (
            <p className="subtitle">Complete seu cadastro para começar.</p>
          )}
        </div>

        <div className="setup-email-badge">
          {user.email}
        </div>

        {error && (
          <div className="error-message" role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            {error}
          </div>
        )}

        <form onSubmit={handleSetup}>
          <div className="form-group">
            <label htmlFor="setup-name">
              <User size={16} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Nome completo
            </label>
            <input
              id="setup-name"
              type="text"
              required
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Seu nome completo"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="setup-password">
              <Lock size={16} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Criar senha
            </label>
            <input
              id="setup-password"
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="setup-confirm">
              <Lock size={16} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Confirmar senha
            </label>
            <input
              id="setup-confirm"
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              minLength={6}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Salvando…' : 'Finalizar cadastro e entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}
